// src/components/ClassTranscript.js
import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { StudentContext } from '../StudentContext';
import {
  subscribeTranscriptLocks,
  updateTranscriptBatch
} from '../FirebaseService';
import './ClassTranscript.css';
import * as XLSX from 'xlsx';

const ClassTranscript = () => {
  const { blockId, classId } = useParams();
  const navigate = useNavigate();
  const { currentYearId, studentsData, transcriptsData, classMap } = useContext(StudentContext);

  const displayClass = classMap[classId] || 'Lớp Không Xác Định';
  const allClassStudents = useMemo(() => studentsData[classId] || [], [studentsData, classId]);

  // 🌟 Công tắc ẩn/hiện thiếu nhi đã Nghỉ luôn (Mặc định: Ẩn)
  const [showInactiveStudents, setShowInactiveStudents] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [transcript, setTranscript] = useState({});
  const [locksData, setLocksData] = useState({ isLocked: false, semester1: {}, semester2: {} });
  const [loading, setLoading] = useState(true);

  // Modal hợp nhất (Quản lý cả tiến trình và kết quả)
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'saving' | 'success' | 'error' | 'warning'
  const [modalMessage, setModalMessage] = useState('');
  const [saveProgress, setSaveProgress] = useState(0);
  const [validationMessage, setValidationMessage] = useState('');

  const isSaving = showModal && modalType === 'saving';

  // Map bảng điểm dự phòng từ transcriptsData trong Context
  const classTranscriptsMap = useMemo(() => {
    const list = transcriptsData?.[classId] || [];
    const map = {};
    list.forEach((item) => {
      if (item.id) map[item.id] = item;
    });
    return map;
  }, [transcriptsData, classId]);

  const initializeTranscript = (studentsList) => {
    const defaultTranscript = {};

    studentsList.forEach((student) => {
      const fallbackTr = classTranscriptsMap[student.id] || {};
      const rawTr =
        student.transcript && Object.keys(student.transcript).length > 0
          ? student.transcript
          : fallbackTr;

      defaultTranscript[student.id] = {
        semester1: {
          oral: String(rawTr.semester1?.oral ?? ''),
          fifteenMinutes: String(rawTr.semester1?.fifteenMinutes ?? ''),
          onePeriod: String(rawTr.semester1?.onePeriod ?? ''),
          exam: String(rawTr.semester1?.exam ?? ''),
          average: String(rawTr.semester1?.average ?? ''),
          diligence: String(rawTr.semester1?.diligence ?? ''),
        },
        semester2: {
          oral: String(rawTr.semester2?.oral ?? ''),
          fifteenMinutes: String(rawTr.semester2?.fifteenMinutes ?? ''),
          onePeriod: String(rawTr.semester2?.onePeriod ?? ''),
          exam: String(rawTr.semester2?.exam ?? ''),
          average: String(rawTr.semester2?.average ?? ''),
          diligence: String(rawTr.semester2?.diligence ?? ''),
        },
        yearly: {
          average: String(rawTr.yearly?.average ?? ''),
          diligence: String(rawTr.yearly?.diligence ?? ''),
          rank: String(rawTr.yearly?.rank ?? ''),
          oralExam: String(rawTr.yearly?.oralExam ?? ''),
        },
      };
    });

    const rankMap = calculateRank(defaultTranscript);
    Object.keys(defaultTranscript).forEach((studentId) => {
      defaultTranscript[studentId].yearly.rank =
        rankMap[studentId] || defaultTranscript[studentId].yearly.rank || '';
    });

    return defaultTranscript;
  };

  // Khi chuyển sang lớp khác thì tắt chế độ sửa cũ
  useEffect(() => {
    setIsEditing(false);
    setValidationMessage('');
  }, [classId]);

  // Khởi tạo bảng điểm cho toàn bộ học sinh trong lớp khi không ở chế độ đang sửa
  useEffect(() => {
    if (!isEditing && allClassStudents && allClassStudents.length >= 0) {
      setTranscript(initializeTranscript(allClassStudents));
      setLoading(false);
    }
  }, [allClassStudents, classTranscriptsMap, classId, isEditing]);

  useEffect(() => {
    if (!currentYearId || !classId) return;

    const unsubscribe = subscribeTranscriptLocks(currentYearId, classId, (newLocks) => {
      setLocksData(newLocks);

      if (newLocks.isLocked && isEditing) {
        setIsEditing(false);
        setValidationMessage('');
        setModalMessage('Bảng điểm vừa bị Quản trị viên khóa. Mọi thay đổi chưa lưu đã bị hủy.');
        setModalType('warning');
        setShowModal(true);
      }
    });

    return () => unsubscribe();
  }, [currentYearId, classId, isEditing]);

  const calculateSemesterAverage = (scores) => {
    const weights = { oral: 1, fifteenMinutes: 1, onePeriod: 2, exam: 4 };
    const requiredFields = ['oral', 'fifteenMinutes', 'onePeriod', 'exam'];

    const allPresent = requiredFields.every((field) => {
      const val = scores[field];
      return val !== '' && !isNaN(parseFloat(val));
    });

    if (!allPresent) return '';

    let totalScore = 0;
    let totalWeight = 0;

    requiredFields.forEach((field) => {
      const scoreVal = parseFloat(scores[field]);
      const w = weights[field];
      totalScore += scoreVal * w;
      totalWeight += w;
    });

    return (totalScore / totalWeight).toFixed(2);
  };

  const calculateYearlyAverage = (sem1AvgStr, sem2AvgStr) => {
    const sem1Avg = parseFloat(sem1AvgStr);
    const sem2Avg = parseFloat(sem2AvgStr);
    if (!isNaN(sem1Avg) && !isNaN(sem2Avg)) {
      return ((sem1Avg + sem2Avg) / 2).toFixed(2);
    }
    return '';
  };

  const calculateRank = (currentTranscript) => {
    const rankMap = {};
    Object.keys(currentTranscript).forEach((studentId) => { rankMap[studentId] = ''; });

    const studentsWithScores = Object.keys(currentTranscript)
      .map((studentId) => {
        const yearlyData = currentTranscript[studentId]?.yearly || {};
        const yearlyAvg = parseFloat(yearlyData.average) || 0;
        return {
          studentId,
          yearlyAvg,
          diligence: String(yearlyData.diligence || '').toLowerCase(),
        };
      })
      .filter((student) => student.yearlyAvg > 0);

    if (studentsWithScores.length === 0) return rankMap;

    studentsWithScores.sort((a, b) => b.yearlyAvg - a.yearlyAvg);

    let position = 1;
    let currentRank = 1;
    let previousAvg = null;

    studentsWithScores.forEach((student) => {
      const { studentId, yearlyAvg, diligence } = student;

      if (diligence === 'kém' || yearlyAvg < 5.0) {
        rankMap[studentId] = 'Ở lại lớp';
        return;
      }

      if (previousAvg !== null && yearlyAvg !== previousAvg) {
        currentRank = position;
      }

      if (currentRank === 1) rankMap[studentId] = 'Hạng 1';
      else if (currentRank === 2) rankMap[studentId] = 'Hạng 2';
      else if (currentRank === 3) rankMap[studentId] = 'Hạng 3';
      else rankMap[studentId] = 'Được lên lớp';

      previousAvg = yearlyAvg;
      position++;
    });

    return rankMap;
  };

  const handleScoreChange = (studentId, semester, field, value) => {
    let correctedValue = value;
    let message = '';

    if (value.includes(',')) {
      correctedValue = value.replace(/,/g, '.');
      message = 'Đã tự động đổi dấu phẩy (,) thành dấu chấm (.).';
    }

    const typingPattern = /^[0-9]*\.?[0-9]*$/;
    if (!typingPattern.test(correctedValue)) {
      setValidationMessage('Điểm chỉ nên chứa số và dấu chấm (.).');
    } else {
      const numValue = parseFloat(correctedValue);
      if (correctedValue !== '' && (numValue < 0 || numValue > 10)) {
        message = 'Điểm phải nằm trong khoảng từ 0 đến 10.';
      } else if (!message) {
        setValidationMessage('');
      }
    }

    if (message) {
      setValidationMessage(message);
      setTimeout(() => setValidationMessage((prev) => (prev === message ? '' : prev)), 3000);
    }

    setTranscript((prev) => {
      const updatedStudent = JSON.parse(JSON.stringify(prev[studentId] || { semester1: {}, semester2: {}, yearly: {} }));

      if (semester === 'yearly' && field === 'oralExam') {
        updatedStudent.yearly.oralExam = correctedValue;
      } else {
        if (!updatedStudent[semester]) updatedStudent[semester] = {};
        updatedStudent[semester][field] = correctedValue;

        const sem1Avg = calculateSemesterAverage(updatedStudent.semester1 || {});
        const sem2Avg = calculateSemesterAverage(updatedStudent.semester2 || {});

        updatedStudent.semester1.average = sem1Avg;
        updatedStudent.semester2.average = sem2Avg;
        updatedStudent.yearly.average = calculateYearlyAverage(sem1Avg, sem2Avg);
      }

      const updatedTranscript = { ...prev, [studentId]: updatedStudent };
      const rankMap = calculateRank(updatedTranscript);

      Object.keys(updatedTranscript).forEach((id) => {
        if (updatedTranscript[id]?.yearly) {
          updatedTranscript[id].yearly.rank = rankMap[id] || '';
        }
      });

      return updatedTranscript;
    });
  };

  const handleDiligenceChange = (studentId, semester, value) => {
    setTranscript((prev) => {
      const updatedStudent = JSON.parse(JSON.stringify(prev[studentId] || { semester1: {}, semester2: {}, yearly: {} }));
      if (!updatedStudent[semester]) updatedStudent[semester] = {};
      updatedStudent[semester].diligence = value;

      const updatedTranscript = { ...prev, [studentId]: updatedStudent };
      const rankMap = calculateRank(updatedTranscript);

      Object.keys(updatedTranscript).forEach((id) => {
        if (updatedTranscript[id]?.yearly) {
          updatedTranscript[id].yearly.rank = rankMap[id] || '';
        }
      });

      return updatedTranscript;
    });
  };

  const handleRecalculate = () => {
    setTranscript((prev) => {
      const updatedTranscript = JSON.parse(JSON.stringify(prev));

      Object.keys(updatedTranscript).forEach((studentId) => {
        const student = updatedTranscript[studentId];
        const sem1Avg = calculateSemesterAverage(student.semester1 || {});
        const sem2Avg = calculateSemesterAverage(student.semester2 || {});

        student.semester1.average = sem1Avg;
        student.semester2.average = sem2Avg;
        student.yearly.average = calculateYearlyAverage(sem1Avg, sem2Avg);
      });

      const rankMap = calculateRank(updatedTranscript);
      Object.keys(updatedTranscript).forEach((id) => {
        if (updatedTranscript[id]?.yearly) {
          updatedTranscript[id].yearly.rank = rankMap[id] || '';
        }
      });

      return updatedTranscript;
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setValidationMessage('');
    setTranscript(initializeTranscript(allClassStudents));
  };

  const handleEdit = () => {
    if (locksData.isLocked) {
      setModalMessage('Bảng điểm đã bị khóa. Vui lòng liên hệ Admin để mở khóa.');
      setModalType('error');
      setShowModal(true);
      return;
    }
    setIsEditing(true);
  };

  const handleSave = async () => {
    let hasInvalid = false;
    for (const sId in transcript) {
      for (const sem of ['semester1', 'semester2']) {
        for (const f of ['oral', 'fifteenMinutes', 'onePeriod', 'exam']) {
          const score = transcript[sId]?.[sem]?.[f];
          if (score && score !== '') {
            const num = parseFloat(score);
            if (isNaN(num) || num < 0 || num > 10) {
              hasInvalid = true;
              break;
            }
          }
        }
      }
    }

    if (hasInvalid) {
      setModalType('error');
      setModalMessage('Điểm số không hợp lệ. Vui lòng kiểm tra lại điểm từ 0 đến 10.');
      setShowModal(true);
      return;
    }

    setShowModal(true);
    setModalType('saving');
    setSaveProgress(15);

    const progressTimer = setInterval(() => {
      setSaveProgress((prev) => {
        if (prev >= 88) return prev;
        return prev + Math.floor(Math.random() * 8 + 5);
      });
    }, 200);

    try {
      const result = await updateTranscriptBatch(currentYearId, classId, transcript);
      clearInterval(progressTimer);
      setSaveProgress(100);

      setTimeout(() => {
        if (result.success) {
          setModalType('success');
          setModalMessage('Bảng điểm đã được lưu thành công vào hệ thống!');
          setIsEditing(false);
          setValidationMessage('');
        } else {
          setModalType('error');
          setModalMessage('Lỗi khi cập nhật bảng điểm: ' + result.message);
        }
      }, 350);
    } catch (error) {
      clearInterval(progressTimer);
      setSaveProgress(0);
      setModalType('error');
      setModalMessage(`Lỗi khi lưu bảng điểm: ${error.message}`);
    }
  };

  const getGivenName = (fullName) => (fullName ? fullName.trim().split(/\s+/).pop() : '');

  // 🌟 Lọc và sắp xếp danh sách học sinh hiển thị dựa trên công tắc Nghỉ luôn
  const sortedStudents = useMemo(() => {
    const filtered = allClassStudents.filter((s) => {
      const st = String(s.status || '').trim().toLowerCase();
      const isDroppedOut = st === 'nghỉ luôn' || st === 'nghỉ học' || st === 'đã nghỉ';
      if (!showInactiveStudents && isDroppedOut) return false;
      return true;
    });

    return [...filtered].sort((a, b) =>
      getGivenName(a.fullName).localeCompare(getGivenName(b.fullName), 'vi')
    );
  }, [allClassStudents, showInactiveStudents]);

  // Đếm số lượng thiếu nhi nghỉ luôn trong lớp
  const inactiveCount = useMemo(() => {
    return allClassStudents.filter((s) => {
      const st = String(s.status || '').trim().toLowerCase();
      return st === 'nghỉ luôn' || st === 'nghỉ học' || st === 'đã nghỉ';
    }).length;
  }, [allClassStudents]);

  const downloadExcel = () => {
    const data = [];
    const header = [
      'STT', 'ID', 'Tên Thánh', 'Họ và Tên',
      'HK1 - Miệng', 'HK1 - 15p', 'HK1 - 1 Tiết', 'HK1 - Thi', 'HK1 - TB', 'HK1 - Chuyên Cần',
      'HK2 - Miệng', 'HK2 - 15p', 'HK2 - 1 Tiết', 'HK2 - Thi', 'HK2 - TB', 'HK2 - Chuyên Cần',
      'Cả Năm - TBCN', 'Cả Năm - Chuyên Cần', 'Cả Năm - Xếp Hạng', 'Cả Năm - Vấn Đáp'
    ];
    data.push(header);

    sortedStudents.forEach((st, idx) => {
      const tr = transcript[st.id] || {};
      data.push([
        idx + 1, st.id, st.holyName || '', st.fullName,
        tr.semester1?.oral || '', tr.semester1?.fifteenMinutes || '', tr.semester1?.onePeriod || '', tr.semester1?.exam || '', tr.semester1?.average || '', tr.semester1?.diligence || '',
        tr.semester2?.oral || '', tr.semester2?.fifteenMinutes || '', tr.semester2?.onePeriod || '', tr.semester2?.exam || '', tr.semester2?.average || '', tr.semester2?.diligence || '',
        tr.yearly?.average || '', tr.yearly?.diligence || '', tr.yearly?.rank || '', tr.yearly?.oralExam || ''
      ]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Bảng Điểm');
    XLSX.writeFile(wb, `BangDiem_${displayClass.replace(/\s+/g, '_')}.xlsx`);
  };

  if (loading) {
    return <div className="class-transcript"><p>⏳ Đang nạp bảng điểm...</p></div>;
  }

  return (
    <div className="class-transcript">
      <h3>Bảng điểm lớp: {displayClass}</h3>

      {isEditing && (
        <p className="decimal-instruction">
          💡 Nhập điểm dùng dấu chấm (.) cho phần thập phân (ví dụ: 8.5). Dấu phẩy (,) sẽ tự đổi thành dấu chấm.
        </p>
      )}
      {validationMessage && <p className="validation-message error">{validationMessage}</p>}

      {allClassStudents.length > 0 ? (
        <div>
          <div className="controls-container">
            <div className="lock-toggle-container">
              <button className="back-button" onClick={() => navigate(`/main/class-list/${blockId}/${classId}`)}>
                Quay lại
              </button>

              {/* 🌟 CÔNG TẮC ẨN / HIỆN THIẾU NHI NGHỈ LUÔN 🌟 */}
              <label className="ct-switch-container" title="Bật/tắt hiển thị các thiếu nhi đã nghỉ luôn">
                <input
                  type="checkbox"
                  checked={showInactiveStudents}
                  onChange={(e) => setShowInactiveStudents(e.target.checked)}
                  className="ct-switch-input"
                />
                <span className="ct-switch-label">
                  {showInactiveStudents
                    ? `👁️ Đang hiện em nghỉ luôn (${inactiveCount})`
                    : `🙈 Ẩn em nghỉ luôn (${inactiveCount})`}
                </span>
              </label>
            </div>

            <div className="button-group main-buttons">
              {isEditing ? (
                <>
                  <button className="save-button" onClick={handleSave} disabled={isSaving}>
                    💾 Lưu
                  </button>
                  <button className="recalculate-button" onClick={handleRecalculate} disabled={isSaving}>
                    Tính lại điểm
                  </button>
                  <button className="cancel-button" onClick={handleCancel} disabled={isSaving}>
                    Hủy
                  </button>
                </>
              ) : (
                <>
                  <button
                    className={`edit-button ${locksData.isLocked ? 'disabled' : ''}`}
                    onClick={handleEdit}
                    disabled={locksData.isLocked}
                  >
                    ✏️ Sửa bảng điểm
                  </button>
                  <button className="download-button" onClick={downloadExcel}>
                    📥 Xuất Excel ({sortedStudents.length})
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="table-wrapper">
            <table className="transcript-table">
              <thead>
                <tr>
                  {/* 🌟 CỘT STT MỚI THÊM 🌟 */}
                  <th rowSpan="2" className="col-stt">STT</th>
                  <th rowSpan="2" className="col-id">ID</th>
                  <th rowSpan="2" className="col-holy">Tên Thánh</th>
                  <th rowSpan="2" className="col-name sticky-col-head">Họ và Tên</th>
                  <th colSpan="6">Học kỳ I</th>
                  <th colSpan="6">Học kỳ II</th>
                  <th colSpan="4">Cả năm</th>
                </tr>
                <tr>
                  <th className="col-score">Miệng</th>
                  <th className="col-score">15 phút</th>
                  <th className="col-score">1 tiết</th>
                  <th className="col-score">Thi</th>
                  <th className="col-avg">TB</th>
                  <th className="col-diligence border-section-end">C. Cần</th>

                  <th className="col-score">Miệng</th>
                  <th className="col-score">15 phút</th>
                  <th className="col-score">1 tiết</th>
                  <th className="col-score">Thi</th>
                  <th className="col-avg">TB</th>
                  <th className="col-diligence border-section-end">C. Cần</th>

                  <th className="col-avg">TBCN</th>
                  <th className="col-diligence">C. Cần</th>
                  <th className="col-rank">XH</th>
                  <th className="col-score">Vấn đáp</th>
                </tr>
              </thead>
              <tbody>
                {sortedStudents.map((student, index) => {
                  const studentTranscript = transcript[student.id] || { semester1: {}, semester2: {}, yearly: {} };
                  const sem1Lock = locksData.semester1 || {};
                  const sem2Lock = locksData.semester2 || {};
                  const stStatus = String(student.status || '').trim().toLowerCase();
                  const isDroppedOut = stStatus === 'nghỉ luôn' || stStatus === 'nghỉ học' || stStatus === 'đã nghỉ';

                  return (
                    <tr key={student.id} className={isDroppedOut ? 'ct-row-inactive' : ''}>
                      {/* 🌟 Ô HIỂN THỊ STT 🌟 */}
                      <td className="col-stt">{index + 1}</td>
                      <td className="col-id">{student.id}</td>
                      <td className="col-holy">{student.holyName || '-'}</td>
                      <td className="col-name sticky-col">
                        {student.fullName}
                        {isDroppedOut && <span className="ct-inactive-tag">Nghỉ luôn</span>}
                      </td>

                      {/* HK1 */}
                      <td className="col-score">
                        {isEditing && !sem1Lock.oral ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={studentTranscript.semester1?.oral || ''}
                            onChange={(e) => handleScoreChange(student.id, 'semester1', 'oral', e.target.value)}
                            className="score-input"
                            maxLength="4"
                          />
                        ) : (studentTranscript.semester1?.oral || '-')}
                      </td>
                      <td className="col-score">
                        {isEditing && !sem1Lock.fifteenMinutes ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={studentTranscript.semester1?.fifteenMinutes || ''}
                            onChange={(e) => handleScoreChange(student.id, 'semester1', 'fifteenMinutes', e.target.value)}
                            className="score-input"
                            maxLength="4"
                          />
                        ) : (studentTranscript.semester1?.fifteenMinutes || '-')}
                      </td>
                      <td className="col-score">
                        {isEditing && !sem1Lock.onePeriod ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={studentTranscript.semester1?.onePeriod || ''}
                            onChange={(e) => handleScoreChange(student.id, 'semester1', 'onePeriod', e.target.value)}
                            className="score-input"
                            maxLength="4"
                          />
                        ) : (studentTranscript.semester1?.onePeriod || '-')}
                      </td>
                      <td className="col-score">
                        {isEditing && !sem1Lock.exam ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={studentTranscript.semester1?.exam || ''}
                            onChange={(e) => handleScoreChange(student.id, 'semester1', 'exam', e.target.value)}
                            className="score-input"
                            maxLength="4"
                          />
                        ) : (studentTranscript.semester1?.exam || '-')}
                      </td>
                      <td className="col-avg average-cell">{studentTranscript.semester1?.average || '-'}</td>
                      <td className="col-diligence border-section-end">
                        {isEditing ? (
                          <select
                            value={studentTranscript.semester1?.diligence || ''}
                            onChange={(e) => handleDiligenceChange(student.id, 'semester1', e.target.value)}
                            className="diligence-select"
                          >
                            <option value="">-</option>
                            <option value="Tốt">Tốt</option>
                            <option value="Khá">Khá</option>
                            <option value="Trung bình">TB</option>
                            <option value="Kém">Kém</option>
                          </select>
                        ) : (studentTranscript.semester1?.diligence || '-')}
                      </td>

                      {/* HK2 */}
                      <td className="col-score">
                        {isEditing && !sem2Lock.oral ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={studentTranscript.semester2?.oral || ''}
                            onChange={(e) => handleScoreChange(student.id, 'semester2', 'oral', e.target.value)}
                            className="score-input"
                            maxLength="4"
                          />
                        ) : (studentTranscript.semester2?.oral || '-')}
                      </td>
                      <td className="col-score">
                        {isEditing && !sem2Lock.fifteenMinutes ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={studentTranscript.semester2?.fifteenMinutes || ''}
                            onChange={(e) => handleScoreChange(student.id, 'semester2', 'fifteenMinutes', e.target.value)}
                            className="score-input"
                            maxLength="4"
                          />
                        ) : (studentTranscript.semester2?.fifteenMinutes || '-')}
                      </td>
                      <td className="col-score">
                        {isEditing && !sem2Lock.onePeriod ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={studentTranscript.semester2?.onePeriod || ''}
                            onChange={(e) => handleScoreChange(student.id, 'semester2', 'onePeriod', e.target.value)}
                            className="score-input"
                            maxLength="4"
                          />
                        ) : (studentTranscript.semester2?.onePeriod || '-')}
                      </td>
                      <td className="col-score">
                        {isEditing && !sem2Lock.exam ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={studentTranscript.semester2?.exam || ''}
                            onChange={(e) => handleScoreChange(student.id, 'semester2', 'exam', e.target.value)}
                            className="score-input"
                            maxLength="4"
                          />
                        ) : (studentTranscript.semester2?.exam || '-')}
                      </td>
                      <td className="col-avg average-cell">{studentTranscript.semester2?.average || '-'}</td>
                      <td className="col-diligence border-section-end">
                        {isEditing ? (
                          <select
                            value={studentTranscript.semester2?.diligence || ''}
                            onChange={(e) => handleDiligenceChange(student.id, 'semester2', e.target.value)}
                            className="diligence-select"
                          >
                            <option value="">-</option>
                            <option value="Tốt">Tốt</option>
                            <option value="Khá">Khá</option>
                            <option value="Trung bình">TB</option>
                            <option value="Kém">Kém</option>
                          </select>
                        ) : (studentTranscript.semester2?.diligence || '-')}
                      </td>

                      {/* CẢ NĂM */}
                      <td className="col-avg average-cell">{studentTranscript.yearly?.average || '-'}</td>
                      <td className="col-diligence">
                        {isEditing ? (
                          <select
                            value={studentTranscript.yearly?.diligence || ''}
                            onChange={(e) => handleDiligenceChange(student.id, 'yearly', e.target.value)}
                            className="diligence-select"
                          >
                            <option value="">-</option>
                            <option value="Tốt">Tốt</option>
                            <option value="Khá">Khá</option>
                            <option value="Trung bình">TB</option>
                            <option value="Kém">Kém</option>
                          </select>
                        ) : (studentTranscript.yearly?.diligence || '-')}
                      </td>
                      <td className="col-rank rank-cell">{studentTranscript.yearly?.rank || '-'}</td>
                      <td className="col-score">
                        {isEditing ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={studentTranscript.yearly?.oralExam || ''}
                            onChange={(e) => handleScoreChange(student.id, 'yearly', 'oralExam', e.target.value)}
                            className="score-input"
                            maxLength="4"
                          />
                        ) : (studentTranscript.yearly?.oralExam || '-')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p>Chưa có dữ liệu học sinh cho lớp {displayClass}.</p>
      )}

      {/* MODAL HỢP NHẤT */}
      {showModal && (
        <div className="unified-modal-overlay">
          <div className="unified-modal-card">
            {modalType === 'saving' && (
              <div className="modal-state-saving">
                <div className="modal-spinner"></div>
                <h4 className="modal-title">Đang lưu bảng điểm, vui lòng chờ...</h4>
                <p className="modal-warning">
                  ⚠️ Vui lòng <strong>không tắt trình duyệt</strong> hoặc tải lại trang!
                </p>
                <div className="progress-container">
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${saveProgress}%` }}
                  ></div>
                </div>
                <div className="progress-text">{saveProgress}%</div>
              </div>
            )}

            {modalType === 'success' && (
              <div className="modal-state-success">
                <div className="modal-status-icon success-icon">✓</div>
                <h4 className="modal-title success-text">Lưu Điểm Thành Công!</h4>
                <p className="modal-desc">{modalMessage}</p>
                <button
                  onClick={() => setShowModal(false)}
                  className="modal-action-button"
                >
                  Xác nhận / Đóng
                </button>
              </div>
            )}

            {(modalType === 'error' || modalType === 'warning') && (
              <div className={`modal-state-${modalType}`}>
                <div className={`modal-status-icon ${modalType}-icon`}>
                  {modalType === 'error' ? '✕' : '⚠️'}
                </div>
                <h4 className="modal-title">
                  {modalType === 'error' ? 'Có lỗi xảy ra' : 'Lưu ý'}
                </h4>
                <p className="modal-desc">{modalMessage}</p>
                <button
                  onClick={() => setShowModal(false)}
                  className="modal-action-button"
                >
                  Đóng
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassTranscript;